/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { Observable } from "@vienna-hypertext/observable";
import throttle from "lodash/throttle";

import { Point } from "src/classes/plate/lib/types";
import { Plate } from "src/classes/plate/Plate";
import { MathUtils } from "src/classes/utilities/MathUtils";

export type MouseTrackerSnapshot = {
  position: Point;
};
export class MouseTracker extends Observable<MouseTrackerSnapshot> {
  canvasPosition?: Point;

  plate: Plate;

  private constructor(snapshot: MouseTrackerSnapshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
  }

  /* -------------------------------------------------- */
  /*                        State                       */
  /* -------------------------------------------------- */

  private setCanvasPosition = (canvasRef: React.RefObject<HTMLDivElement>): void => {
    if (!canvasRef.current) return;
    const { x, y } = canvasRef.current.getBoundingClientRect();

    // update mouse position
    if (this.canvasPosition) {
      // get diff from old and new canvas position
      const diffX = this.canvasPosition.x - x;
      const diffY = this.canvasPosition.y - y;
      // set mouse position based on diff
      this.setState({ position: { x: this.snapshot.position.x + diffX, y: this.snapshot.position.y + diffY } });
    }
    this.canvasPosition = { x, y };
  };

  public updateMousePositionFromEvent = (e: MouseEvent | React.MouseEvent): void => {
    if (!this.canvasPosition) return;
    const relativeX = e.clientX - this.canvasPosition.x;
    const relativeY = e.clientY - this.canvasPosition.y;

    const position = {
      x: MathUtils.constrainValueToBounds(relativeX, 0, this.plate.snapshot.width),
      y: MathUtils.constrainValueToBounds(relativeY, 0, this.plate.snapshot.height),
    };
    this.setState({ position });
  };
  /* -------------------------------------------------- */
  /*                        Utils                       */
  /* -------------------------------------------------- */

  public getHasMovedFromPoint = (point?: Point): boolean => {
    if (!point) return true;
    return point.x !== this.snapshot.position.x || point.y !== this.snapshot.position.y;
  };

  /* -------------------------------------------------- */
  /*                   Event Handlers                   */
  /* -------------------------------------------------- */

  public handleMouseMove = (e: MouseEvent): void => {
    this.onScroll.flush(); // ensures that if moving the mouse while scrolling, we update the canvasDOMRect
    this.updateMousePositionFromEvent(e);
  };

  public onScroll = throttle(this.setCanvasPosition, 500);

  public onResizeWindow = (canvasRef: React.RefObject<HTMLDivElement>): void => {
    this.setCanvasPosition(canvasRef);
  };

  public onCanvasMounts = (canvasRef: React.RefObject<HTMLDivElement>): void => {
    this.setCanvasPosition(canvasRef);
  };

  /* -------------------------------------------------- */
  /*                  Static Methods                    */
  /* -------------------------------------------------- */

  public static create(plate: Plate): MouseTracker {
    const initialSnapshot: MouseTrackerSnapshot = {
      position: { x: 0, y: 0 },
    };
    return new MouseTracker(initialSnapshot, plate);
  }
}
